import React from 'react';
import { SearchableDropdown } from './SearchableDropdown';
import { MultiSelectDropdown } from './MultiSelectDropdown';

// No extra styling any more (W-M1). The 3px "stand out" border made the first three filters on
// Quantified, Routine and Assets look like a different control from the light-bordered ones next
// to them - one filter row, three select styles. Every select in a filter row now has one look
// (see `.wrap-filter-grid` in tailwind.css); this wrapper is kept so call sites need not change.
const STANDOUT_DROPDOWN_STYLE: React.CSSProperties = {};

// Props for SearchableDropdown wrapper
interface StandoutSearchableDropdownProps<T> {
  type: 'searchable';
  options: T[];
  value: string | number | undefined | null;
  onChange: (value: string) => void;
  formatOption: (option: T) => string;
  formatDisplay?: (option: T) => string;
  getValue: (option: T) => string | number;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  style?: React.CSSProperties;
  readOnly?: boolean;
  readOnlyStyle?: React.CSSProperties;
  label?: string;
  isRequired?: boolean;
  noOptionsMessage?: string;
  fetchOptions?: (query: string) => Promise<T[]>;
  minCharsForSearch?: number;
  allowClear?: boolean;
  preserveOptionsOrder?: boolean;
}

// Props for MultiSelectDropdown wrapper
interface StandoutMultiSelectDropdownProps<T> {
  type: 'multiselect';
  options: T[];
  value: (string | number)[] | undefined | null;
  onChange: (value: (string | number)[]) => void;
  formatOption: (option: T) => string;
  formatDisplay?: (option: T) => string;
  getValue: (option: T) => string | number;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  style?: React.CSSProperties;
  readOnly?: boolean;
  readOnlyStyle?: React.CSSProperties;
  label?: string;
  isRequired?: boolean;
  noOptionsMessage?: string;
  maxDisplayItems?: number;
  showSelectedCount?: boolean;
  /** Render options as given instead of re-sorting. Use where order is meaningful (statuses). */
  preserveOptionsOrder?: boolean;
  /** Show the in-dropdown search box. Turn off for short fixed option sets (e.g. two choices). */
  showSearch?: boolean;
}

type StandoutDropdownProps<T> = StandoutSearchableDropdownProps<T> | StandoutMultiSelectDropdownProps<T>;

/**
 * StandoutDropdown - A wrapper component that applies consistent "stand out" styling
 * to SearchableDropdown or MultiSelectDropdown components.
 * 
 * This component reduces code duplication by centralizing the blue highlight styling
 * used across filter components.
 */
export function StandoutDropdown<T>(props: StandoutDropdownProps<T>) {
  const style = 'style' in props ? props.style : undefined;
  
  // Merge dropdown styles - just a subtle border to distinguish it
  const mergedDropdownStyle: React.CSSProperties = {
    ...STANDOUT_DROPDOWN_STYLE,
    ...style
  };

  if (props.type === 'searchable') {
    const searchableProps = props as StandoutSearchableDropdownProps<T>;
    return (
      <SearchableDropdown<T>
        options={searchableProps.options}
        value={searchableProps.value}
        onChange={searchableProps.onChange}
        formatOption={searchableProps.formatOption}
        formatDisplay={searchableProps.formatDisplay}
        getValue={searchableProps.getValue}
        placeholder={searchableProps.placeholder}
        disabled={searchableProps.disabled}
        required={searchableProps.required}
        className={searchableProps.className}
        readOnly={searchableProps.readOnly}
        readOnlyStyle={searchableProps.readOnlyStyle}
        label={searchableProps.label}
        isRequired={searchableProps.isRequired}
        noOptionsMessage={searchableProps.noOptionsMessage}
        fetchOptions={searchableProps.fetchOptions}
        minCharsForSearch={searchableProps.minCharsForSearch}
        allowClear={searchableProps.allowClear}
        preserveOptionsOrder={searchableProps.preserveOptionsOrder}
        style={mergedDropdownStyle}
      />
    );
  } else {
    const multiselectProps = props as StandoutMultiSelectDropdownProps<T>;
    return (
      <MultiSelectDropdown<T>
        options={multiselectProps.options}
        value={multiselectProps.value}
        onChange={multiselectProps.onChange}
        formatOption={multiselectProps.formatOption}
        formatDisplay={multiselectProps.formatDisplay}
        getValue={multiselectProps.getValue}
        placeholder={multiselectProps.placeholder}
        disabled={multiselectProps.disabled}
        required={multiselectProps.required}
        className={multiselectProps.className}
        readOnly={multiselectProps.readOnly}
        readOnlyStyle={multiselectProps.readOnlyStyle}
        label={multiselectProps.label}
        isRequired={multiselectProps.isRequired}
        noOptionsMessage={multiselectProps.noOptionsMessage}
        maxDisplayItems={multiselectProps.maxDisplayItems}
        showSelectedCount={multiselectProps.showSelectedCount}
        preserveOptionsOrder={multiselectProps.preserveOptionsOrder}
        showSearch={multiselectProps.showSearch}
        style={mergedDropdownStyle}
      />
    );
  }
}

